/**
 * Inline Prompt Storage
 *
 * Persists inline prompts per PDF document in IndexedDB.
 * Prompts are stored with document-relative positions so they
 * correctly track their anchor points when scrolling.
 */

import { createStore, get, set, del, keys } from "idb-keyval";

// Dedicated store for inline prompts
const inlinePromptStore = createStore("orbit-pdf-inline-prompts", "store");

/**
 * Stored inline prompt data structure.
 * Positions are stored as page-relative coordinates for zoom-invariant positioning.
 */
export interface StoredInlinePrompt {
  id: string;
  /**
   * @deprecated Use pageIndex + pageRelativeTop instead.
   * Kept for backwards compatibility during migration.
   */
  top: number;
  /** Zero-indexed page number where this prompt is anchored */
  pageIndex: number;
  /** Normalized position within page (0-1), where 0 is top and 1 is bottom */
  pageRelativeTop: number;
  /** Scale at which this prompt was created (for migration of legacy prompts) */
  createdAtScale?: number;
  front: string;
  back: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Orbit Task ID if this prompt has been persisted to the database.
   * Links the local inline prompt to its Orbit task for sync/review.
   */
  orbitTaskId?: string;
}

export type DocInlinePrompts = StoredInlinePrompt[];

/**
 * Load all inline prompts for a document.
 */
export async function loadInlinePrompts(docId: string): Promise<DocInlinePrompts> {
  return (await get<DocInlinePrompts>(docId, inlinePromptStore)) ?? [];
}

/**
 * Save all inline prompts for a document.
 * Replaces any existing prompts.
 */
export async function saveInlinePrompts(
  docId: string,
  prompts: DocInlinePrompts
): Promise<void> {
  await set(docId, prompts, inlinePromptStore);
}

/**
 * Delete all inline prompts for a document.
 */
export async function clearInlinePrompts(docId: string): Promise<void> {
  await del(docId, inlinePromptStore);
}

/**
 * Check if a document has any saved inline prompts.
 */
export async function hasInlinePrompts(docId: string): Promise<boolean> {
  const prompts = await loadInlinePrompts(docId);
  return prompts.length > 0;
}

/**
 * Remove a single inline prompt by its Orbit Task ID.
 * Used when a prompt is deleted from Collections to keep IndexedDB in sync.
 */
export async function removeInlinePromptByOrbitTaskId(
  docId: string,
  orbitTaskId: string
): Promise<void> {
  const prompts = await loadInlinePrompts(docId);
  const next = prompts.filter((p) => p.orbitTaskId !== orbitTaskId);
  if (next.length === 0) {
    await clearInlinePrompts(docId);
  } else {
    await saveInlinePrompts(docId, next);
  }
}

/**
 * List all document IDs that have inline prompts stored.
 * Used for global backfill of unsynced prompts.
 */
export async function listAllDocIdsWithInlinePrompts(): Promise<string[]> {
  return (await keys(inlinePromptStore)) as string[];
}

/**
 * Legacy prompt structure before page-relative positioning.
 * Used for migration detection.
 */
interface LegacyStoredInlinePrompt {
  id: string;
  top: number;
  pageIndex?: number;
  pageRelativeTop?: number;
  createdAtScale?: number;
  front: string;
  back: string;
  createdAt: number;
  updatedAt: number;
  orbitTaskId?: string;
}

/**
 * Check if a stored prompt needs migration to page-relative coordinates.
 */
export function promptNeedsMigration(
  prompt: LegacyStoredInlinePrompt
): boolean {
  return (
    prompt.pageRelativeTop === undefined ||
    prompt.pageIndex === undefined ||
    prompt.pageIndex === null
  );
}

/**
 * Migrate a legacy prompt to page-relative coordinates.
 *
 * @param prompt - Legacy prompt with absolute `top` position
 * @param pageHeights - Array of page heights at current scale
 * @param currentScale - Current viewer scale
 * @returns Migrated prompt with page-relative coordinates
 */
export function migratePromptToPageRelative(
  prompt: LegacyStoredInlinePrompt,
  pageHeights: number[],
  currentScale: number
): StoredInlinePrompt {
  // If already migrated, return as-is
  if (!promptNeedsMigration(prompt)) {
    return prompt as StoredInlinePrompt;
  }

  // Find which page contains this prompt based on absolute top
  let cumulativeHeight = 0;
  let pageIndex = 0;
  const scale = prompt.createdAtScale || currentScale;

  for (let i = 0; i < pageHeights.length; i++) {
    const pageHeight = pageHeights[i];
    if (prompt.top < cumulativeHeight + pageHeight) {
      pageIndex = i;
      break;
    }
    cumulativeHeight += pageHeight;
    if (i === pageHeights.length - 1) {
      pageIndex = i; // Last page
    }
  }

  // Calculate relative position within page
  const pageHeight = pageHeights[pageIndex] || 0;
  const offsetInPage = prompt.top - cumulativeHeight;
  const pageRelativeTop =
    pageHeight > 0
      ? Math.min(1, Math.max(0, offsetInPage / pageHeight))
      : 0;

  return {
    ...prompt,
    pageIndex,
    pageRelativeTop,
    createdAtScale: scale,
  };
}
